import {
    Form,
    Formik,
    type FormikConfig,
    type FormikFormProps,
    type FormikHelpers,
    type FormikValues,
} from "formik"
import { type z, type ZodType } from "zod"
import { functionChildren } from "../../utils/functionChildren"
import { zodFormikValidation } from "./constants/adapter"
import { FocusFieldError } from "./FocusFieldError"

export type ZodFormikProps<TFormValues extends FormikValues, TSchema extends ZodType> = Omit<
    FormikConfig<TFormValues>,
    "validationSchema" | "onSubmit"
> & {
    validationSchema: TSchema
    onSubmit: (
        parsedValues: z.infer<TSchema>,
        formValues: TFormValues,
        formikHelpers: FormikHelpers<TFormValues>
    ) => void | Promise<unknown>
    formProps?: FormikFormProps
}

export function ZodFormik<TValues extends FormikValues, TSchema extends ZodType>({
    onSubmit,
    validationSchema,
    children,
    formProps,
    ...props
}: ZodFormikProps<TValues, TSchema>) {
    const handleSubmit: FormikConfig<TValues>["onSubmit"] = async (values, formikHelpers) => {
        return onSubmit(await validationSchema.parseAsync(values), values, formikHelpers)
    }

    return (
        <Formik<TValues>
            {...props}
            validationSchema={zodFormikValidation(validationSchema)}
            onSubmit={handleSubmit}
        >
            {(formikProps) => (
                <Form
                    method="POST"
                    {...formProps}
                >
                    <FocusFieldError />
                    {functionChildren(children, formikProps)}
                </Form>
            )}
        </Formik>
    )
}
